import React, { useCallback, useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import { toast } from "react-toastify";
import * as api from "../../redux/api";
import { reactIcons } from "../../utils/icons";
import { debounce } from "lodash";
import CustomNoRowsOverlay from "../../utils/styles";
import { useNavigate } from "react-router-dom";
import OrganizationModal from "../../components/modals/OrganizationModal";
// import BackButton from "../../components/BackButton";

import Tooltip from "@mui/material/Tooltip";

const RefineryRequest = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeType, setActiveType] = useState("All");
  const [queryParams, setQueryParams] = useState({});
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyUser, setVerifyUser] = useState(null);

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    setActiveType(selectedValue);
    let queryParam = {};

    if (selectedValue === "All") {
      queryParam = {};
    } else {
      queryParam = { refinedStatus: selectedValue };
    }

    setQueryParams(queryParam);
  };

  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 9
  });
  const [rowCountState, setRowCountState] = useState(0);

  const columns = [
    {
      field: "_id",
      headerName: "S.No.",
      type: "number",
      headerAlign: "center",
      align: "center",
      width: 100
    },
    {
      field: "fullName",
      headerName: "Type",
      type: "string",
      flex: 1,
      valueGetter: (params) => `${params.row.entityType || "-"}  `
    },
    {
      field: "email",
      headerName: "ShipmentId",
      type: "string",
      flex: 4,
      headerAlign: "center",
      align: "center",

      valueGetter: (params) => params.row.shipmentId || "-"
    },
    // {
    //   field: "phoneNumber",
    //   headerName: "Mobile",
    //   type: "number",
    //   headerAlign: "left",
    //   align: "left",
    //   flex: 2,
    //   valueFormatter: (params) => (params.value ? params.value : "-")
    // },
    // {
    //   field: "After Refined",
    //   headerName: "After Refined",
    //   type: "string",
    //   flex: 2,
    //   valueGetter: (params) => params.row.afterRefined
    // },
    {
      field: "type",
      headerName: "Current Status",
      type: "string",
      flex: 3,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => params.row.consignmentStatus
    },
    {
      field: "View",
      headerName: "View",
      type: "string",
      headerAlign: "center",
      align: "center",

      flex: 1,
      renderCell: (params) => {
        return [
          <div
            key={params.row.id}
            onClick={() => {
              navigate(`/refinery-consignment-request/${params.row.id}`);
            }}
            style={{ cursor: "pointer" }}
          >
            {reactIcons.eyeVisible}
          </div>
        ];
      }
    },

    {
      field: "action",
      headerName: "Action",
      type: "actions",
      flex: 3,
      renderCell: (params) => {
        const status = params.row.refinedDocumentStatus;

        if (status === "APPROVED") {
          return (
            <span className="text-green-600 font-semibold pl-2">Approved</span>
          );
        }

        if (status === "PENDING") {
          return (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setVerifyModalOpen(true);
                  setVerifyUser(params.row);
                }}
                className="bg-green-600 px-4 py-2 text-white rounded-lg"
              >
                Verify
              </button>
              <button
                onClick={() => {
                  setRejectModalOpen(true);
                  setSelectedUser(params.row);
                }}
                className="bg-red-600 px-4 py-2 text-white rounded-lg"
              >
                Reject
              </button>
            </div>
          );
        }

        return (
          <Tooltip
            title={
              params.row.refinedDocumentRejectionReason || "No reason provided"
            }
            arrow
            className="cursor-pointer"
          >
            <span className="text-red-600 font-semibold pl-2 cursor-help">
              Rejected
            </span>
          </Tooltip>
        );
      }
    }
  ];

  const handleReject = async (user, reason) => {
    try {
      const updatedRows = rows.map((r) =>
        r.shipmentId === user.shipmentId
          ? {
              ...r,
              refinedDocumentStatus: "REJECTED",
              refinedDocumentRejectionReason: reason
            }
          : r
      );
      setRows(updatedRows);
      await api.verifyOrRejectRefinery(
        user.shipmentId || user.shipmentId,
        "REJECTED",
        {
          status: "false",
          RefinedDocumentRejectedReasons: rejectReason
        }
      );
      toast.success(`User has been rejected.`);
      setRejectModalOpen(false);
      setSelectedUser(null);
      setRejectReason("");
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.message || "Failed to reject user.");
      console.error(error);
    }
  };

  const handleVerify = async (user) => {
    try {
      const updatedRows = rows.map((r) =>
        r.shipmentId === user.shipmentId
          ? { ...r, refinedDocumentStatus: "APPROVED" }
          : r
      );
      setRows(updatedRows);
      await api.verifyOrRejectRefinery(
        user.shipmentId || user.shipmentId,
        "APPROVED",
        {
          status: "true"
        }
      );
      toast.success(`User has been verified.`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.message || "Failed to verify user.");
      console.error(error);
    }
  };

  const getUserData = async () => {
    setIsLoading(true);
    const response = await api.getRefineryReq(
      paginationModel.page,
      paginationModel.pageSize,
      searchText,
      queryParams,
      ""
    );
    const { data, status } = response;
    if (status >= 200 && status < 300) {
      setIsLoading(false);
      setRowCountState(data.count);
      setRows(
        data.data.map((item, index) => {
          return {
            ...item,
            _id: paginationModel.page * 10 + index + 1,
            verificationStatus: item.verificationStatus || "Pending"
          };
        })
      );
    } else {
      setIsLoading(false);
      toast.error(data.message);
    }
  };
  // eslint-disable-next-line
  const debouncedFetchUsers = useCallback(
    debounce(() => getUserData(), 500),
    [paginationModel, queryParams, searchText]
  );

  useEffect(() => {
    debouncedFetchUsers();
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [debouncedFetchUsers]);

  const handleDownloadCSV = async () => {
    try {
      const status = activeType !== "All" ? activeType : "";

      const response = await api.downloadRefineryCsv(status);

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const fileName = `Refinery_Export_${status || "All"}.xlsx`;
      a.download = fileName;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV Download Error:", err);
      toast.error("Failed to download CSV");
    }
  };

  return (
    <>
      <div className="w-full mt-10 data-grid">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              {/* <BackButton /> */}
              <h4>Refinery Requests</h4>
            </div>
            <div className="flex flex-col justify-end items-end gap-2">
              <div className="flex gap-2">
                <button
                  className="bg-primary-100 font-poppins text-white px-4 h-11 rounded"
                  onClick={handleDownloadCSV}
                >
                  Download CSV
                </button>
                <div className="relative">
                  <input
                    className="w-[250px] h-[45px] bg-transparent"
                    type="text"
                    value={searchText}
                    placeholder="Search"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <span className="absolute text-18 top-[calc(50%-9px)] right-2">
                    {reactIcons.search}
                  </span>
                </div>
                <div className="form-select-custom">
                  <select
                    className="min-w-[150px] w-full h-[45px] bg-white"
                    name="usersType"
                    id="usersType"
                    value={activeType}
                    onChange={handleSelectChange}
                  >
                    <option value="All">All</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Verified</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                  <span className="ay-center right-2 text-zinc-500 text-[20px] pointer-events-none">
                    {reactIcons.arrowDown}
                  </span>
                </div>
              </div>
              {rejectModalOpen && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg w-[400px]">
                    <h2 className="text-xl font-semibold mb-4">Reject User</h2>
                    <p className="mb-2">
                      Are you sure you want to reject{" "}
                      <strong>{selectedUser.name}</strong>?
                    </p>
                    <label className="block mb-2 font-medium">
                      Reason for rejection:
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded p-2"
                      rows="4"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Write your reason here..."
                    ></textarea>
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        className="px-4 py-3 border border-primary-100 rounded-lg"
                        onClick={() => {
                          setRejectModalOpen(false);
                          setSelectedUser(null);
                          setRejectReason("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-3 bg-red-600 text-white rounded-lg disabled:opacity-50"
                        disabled={!rejectReason.trim()}
                        onClick={() => handleReject(selectedUser, rejectReason)}
                      >
                        Confirm Reject
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {verifyModalOpen && verifyUser && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg w-[400px]">
                    <h2 className="text-xl font-poppins font-semibold mb-4">
                      Verify User
                    </h2>
                    <p className="mb-2">
                      Are you sure you want to verify
                      {/* <strong>{verifyUser.name}</strong>? */}
                    </p>
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        className="px-4 py-2 text-primary-100 border border-primary-100 text-18 rounded-lg "
                        onClick={() => {
                          setVerifyModalOpen(false);
                          setVerifyUser(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-3 bg-primary-100 text-18 text-white rounded-lg"
                        onClick={() => {
                          handleVerify(verifyUser);
                          setVerifyModalOpen(false);
                          setVerifyUser(null);
                        }}
                      >
                        Confirm Verify
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="h-[600px] w-full !text-black mt-4">
          <DataGrid
            loading={isLoading}
            rows={rows}
            getRowId={(row) => row._id}
            getEstimatedRowHeight={() => 52}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10
                }
              }
            }}
            rowCount={rowCountState}
            pageSizeOptions={[10]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            slots={{
              loadingOverlay: LinearProgress,
              noRowsOverlay: CustomNoRowsOverlay
            }}
            disableColumnMenu
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: "default"
              },
              "& .MuiDataGrid-root": {
                color: "black"
              },
              "& .MuiDataGrid-cell": {
                color: "black"
              },
              "& .MuiDataGrid-columnHeaders": {
                color: "black"
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "black"
              },
              "& .MuiDataGrid-columnHeader": {
                color: "black"
              },
              "& .MuiTablePagination-root": {
                color: "white"
              },
              "& .MuiSvgIcon-root": {
                color: "white"
              }
            }}
          />
        </div>
      </div>
      {open && (
        <OrganizationModal
          open={open}
          handleClose={() => {
            setOpen(false);
            debouncedFetchUsers();
          }}
        />
      )}
    </>
  );
};

export default RefineryRequest;
